import { IRynReplayEnvelope } from './ryn-replay-batcher';
import { RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE } from './ryn-replay-event-types';

/**
 * Persistent offline buffer for failed replay batches (#30070).
 *
 * In-memory `retryQueue` dies with the tab. This store survives a refresh or
 * offline stretch so buffered batches can ship once the network returns.
 * Cap uses oldest-first eviction so the IDB copy cannot grow without bound on
 * a long outage (#30070 AC).
 */

export const RYN_REPLAY_OFFLINE_DB_NAME = 'ryn-replay-offline';
export const RYN_REPLAY_OFFLINE_STORE_NAME = 'batches';
export const RYN_REPLAY_OFFLINE_DB_VERSION = 1;

/** Soft ceiling on persisted batches. Mirrors the in-memory retry spirit. */
export const RYN_REPLAY_MAX_OFFLINE_BATCHES = 16;

export interface IRynReplayOfflineBatchRecord {
  id: number;
  sessionId: string;
  batch: IRynReplayEnvelope[];
  enqueuedAt: number;
}

export interface IRynReplayOfflineStore {
  enqueue(sessionId: string, batch: IRynReplayEnvelope[]): Promise<void>;
  /** Oldest first. Does not delete. */
  peekOldest(limit: number): Promise<IRynReplayOfflineBatchRecord[]>;
  remove(id: number): Promise<void>;
  count(): Promise<number>;
}

export const offlineBatchHoldsFullSnapshot = (
  batch: IRynReplayEnvelope[]
): boolean =>
  batch.some(
    (envelope) => envelope.type === RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE
  );

/**
 * Evict until `records.length <= max`, oldest-first (lowest id / earliest
 * enqueue). Matches the #30070 AC; the in-memory retry trimmer may still prefer
 * FullSnapshots — that path is tab-local and separate.
 */
export const trimOfflineBatchRecords = (
  records: IRynReplayOfflineBatchRecord[],
  max: number
): IRynReplayOfflineBatchRecord[] => {
  if (max <= 0) {
    return [];
  }
  const next = [...records].sort((a, b) => a.id - b.id);
  if (next.length <= max) {
    return next;
  }
  return next.slice(next.length - max);
};

/** In-memory double for unit tests (and environments without IndexedDB). */
export class MemoryRynReplayOfflineStore implements IRynReplayOfflineStore {
  private nextId = 1;
  private records: IRynReplayOfflineBatchRecord[] = [];

  async enqueue(sessionId: string, batch: IRynReplayEnvelope[]): Promise<void> {
    if (batch.length === 0) {
      return;
    }
    this.records.push({
      id: this.nextId++,
      sessionId,
      batch,
      enqueuedAt: Date.now(),
    });
    this.records = trimOfflineBatchRecords(
      this.records,
      RYN_REPLAY_MAX_OFFLINE_BATCHES
    );
  }

  async peekOldest(limit: number): Promise<IRynReplayOfflineBatchRecord[]> {
    return this.records
      .slice()
      .sort((a, b) => a.id - b.id)
      .slice(0, Math.max(0, limit));
  }

  async remove(id: number): Promise<void> {
    this.records = this.records.filter((r) => r.id !== id);
  }

  async count(): Promise<number> {
    return this.records.length;
  }
}
